<template>
  <div class="rounded-md bg-white p-6 shadow-md">
    <div class="space-y-4">
      <div class="w-full space-y-2">
        <form @submit.prevent="buscarComprobante">
          <div class="flex flex-col items-center gap-x-6 gap-y-2 sm:flex-row">
            <label
              for="nro-comprobante"
              class="whitespace-nowrap text-base font-medium text-gray-700"
              >Nro. comprobante
            </label>
            <input
              id="nro-comprobante"
              v-input-filter="value => /^\d*$/.test(value)"
              class="w-72 appearance-none rounded-md border border-gray-400 p-2 text-gray-900 placeholder-gray-700 transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm"
              v-model="numeroComprobante"
            />
            <button
              class="w-fit space-x-2 whitespace-nowrap rounded-md border px-3 py-2 text-sm font-medium"
              :class="{
                'border-blue-brand-700 bg-blue-brand-500 text-blue-brand-100 transition duration-150 ease-in-out hover:bg-blue-brand-700 focus:outline-none focus:ring focus:ring-blue-200 focus:ring-opacity-50 active:bg-blue-brand-700':
                  numeroComprobante,
                'border-gray-300 bg-gray-100 text-gray-400': !numeroComprobante,
              }"
              :disabled="!numeroComprobante"
              type="submit"
            >
              Buscar
            </button>
          </div>
        </form>
      </div>
      <transition
        enter-active-class="transition-opacity duration-200"
        leave-active-class="transition-opacity duration-200"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <Cargando v-if="isLoading" key="1" />
        <Empty v-else-if="error" key="2" />
        <div v-else-if="comprobanteTerceros" key="3" class="space-y-4">
          <div class="space-y-2">
            <div class="text-lg font-semibold text-gray-900">Datos del comprobante</div>
            <div class="flex flex-wrap gap-y-1 tracking-wide text-gray-700">
              <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                <span class="font-semibold">Nro. comprobante</span>
                <span>{{ comprobante.numero_comprobante }}</span>
              </div>
              <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                <span class="font-semibold">Monto</span>
                <span>{{ comprobante.monto | currency }}</span>
              </div>
              <div v-if="comprobanteTerceros.sede" class="space-x-2">
                <span class="font-semibold">Delegación</span>
                <span class="whitespace-nowrap">{{ comprobanteTerceros.sede.descripcion }}</span>
              </div>
            </div>
          </div>
          <div class="space-y-2">
            <div class="text-lg font-semibold text-gray-900">Datos del titular</div>
            <div class="flex flex-wrap gap-y-1 tracking-wide text-gray-700">
              <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                <span class="font-semibold">CUIT/CUIL</span>
                <span>{{ comprobanteTerceros.cuit_cuil | cuit }}</span>
              </div>
              <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                <span class="font-semibold">Apellido y nombre</span>
                <span>{{ comprobanteTerceros.apellido }}, {{ comprobanteTerceros.nombres }}</span>
              </div>
              <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                <span class="font-semibold">E-mail</span>
                <span>{{ comprobanteTerceros.email }}</span>
              </div>
              <div class="space-x-2">
                <span class="font-semibold">Teléfono</span>
                <span>{{ comprobanteTerceros.telefono }}</span>
              </div>
            </div>
          </div>
          <div class="space-y-2">
            <div class="text-lg font-semibold text-gray-900">Datos de quien abona</div>
            <div class="flex flex-wrap gap-y-1 tracking-wide text-gray-700">
              <div class="space-x-2 after:px-2 after:font-thin after:content-['\2022']">
                <span class="font-semibold">CUIT/CUIL</span>
                <span>{{ comprobanteTerceros.cuit_cuil_pago | cuit }}</span>
              </div>
              <div class="space-x-2">
                <span class="font-semibold">Código de pago electrónico</span>
                <span>{{ comprobante.codigo_pago_electronico }}</span>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap gap-x-10 gap-y-4">
            <div class="min-w-1/4 max-w-full gap-2 sm:max-w-[45%]">
              <div class="text-lg font-semibold text-gray-900">Datos del pago</div>
              <div class="space-x-2">
                <span class="font-semibold">Estado</span>
                <span class="uppercase">{{
                  textoEstadoPago(comprobanteTerceros.pago.estado_id)
                }}</span>
              </div>
              <div class="" v-if="comprobanteTerceros.pago">
                <span class="font-semibold">Observaciones</span>
                <p v-if="comprobanteTerceros.pago.observaciones">
                  {{ comprobanteTerceros.pago.observaciones }}
                </p>
                <p v-else>&ndash;</p>
              </div>
            </div>
            <div class="min-w-1/4 max-w-full gap-2 sm:max-w-[45%]">
              <div class="text-lg font-semibold text-gray-900">Datos del uso</div>
              <div class="space-x-2">
                <span class="font-semibold">Estado</span>
                <span class="uppercase">{{
                  textoEstadoUso(comprobanteTerceros.uso.estado_id)
                }}</span>
              </div>
              <div class="" v-if="comprobanteTerceros.uso">
                <span class="font-semibold">Observaciones</span>
                <p v-if="comprobanteTerceros.uso.observaciones">
                  {{ comprobanteTerceros.uso.observaciones }}
                </p>
                <p v-else>&ndash;</p>
              </div>
            </div>
          </div>
          <MessageCard
            v-if="!comprobantePago || comprobanteUsado"
            type="warning"
            :message="'Sólo se pueden transferir los comprobantes que se encuentren PAGOS y SIN USAR'"
          />
          <div v-else class="space-y-4">
            <div class="mt-1 w-full sm:w-72">
              <label for="sede" class="block text-sm font-medium tracking-wide text-gray-700">
                <span>Nueva sede</span>
              </label>
              <select
                class="mt-1 block w-full appearance-none transition duration-150 ease-in-out sm:text-sm"
                id="sede"
                v-model="nuevaSede"
              >
                <option :value="null" selected disabled>
                  {{ tipoSede(comprobanteTerceros.sede.tipo_id) }}
                  {{ comprobanteTerceros.sede.descripcion }}
                </option>
                <option v-for="sede in sedesElegibles" :key="sede.id" :value="sede">
                  {{ tipoSede(sede.tipo_id) }}
                  {{ sede.descripcion }}
                </option>
              </select>
            </div>
            <div class="flex justify-center">
              <button
                class="inline-flex items-center space-x-2 rounded-md px-5 py-2 text-sm font-medium uppercase tracking-wide text-white transition duration-150 ease-in-out focus:outline-none"
                :class="
                  nuevaSede
                    ? 'bg-colescba-blue-500 hover:bg-colescba-blue-600 focus:ring-1 focus:ring-colescba-blue-500 focus:ring-offset-1'
                    : 'cursor-not-allowed bg-gray-400'
                "
                @click="showConfirmModal = true"
                :disabled="!nuevaSede"
                type="button"
              >
                Transferir
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <ConfirmationModal
      :isOpen="showConfirmModal"
      @save="guardarNuevaSede"
      @modal:cancel="showConfirmModal = false"
    >
      <span v-if="nuevaSede">
        El comprobante nro. {{ comprobante.numero_comprobante }} será transferido de la
        {{ tipoSede(comprobanteTerceros.sede.tipo_id) }}
        {{ comprobanteTerceros.sede.descripcion }} a la {{ tipoSede(nuevaSede.tipo_id) }}
        {{ nuevaSede.descripcion }}.
      </span>
    </ConfirmationModal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { useService } from '@colescba/app-vue'
import { useSession, useAcreditacionServicio } from '@colescba/ui'

import { useAccessControl } from '@/composables'

import Cargando from '@/components/Core/Cargando'
import Empty from '@/components/Core/Empty'
import MessageCard from '@/components/Core/MessageCard'
import ConfirmationModal from '@/components/Core/ConfirmationModal'

export default {
  name: 'TransferenciaSedes',

  mixins: [useService('portalPagos'), useSession(), useAcreditacionServicio(), useAccessControl()],

  components: { Cargando, Empty, MessageCard, ConfirmationModal },

  data() {
    return {
      isLoading: true,
      numeroComprobante: null,
      comprobanteTerceros: null,
      error: null,
      sedes: [],
      nuevaSede: null,
      showConfirmModal: false,
    }
  },

  async mounted() {
    this.fetchEstadosUso({ cuitCuil: this.user.cuitCuil.numero, portalPagos: this.portalPagos })
    this.fetchEstadosPago({ cuitCuil: this.user.cuitCuil.numero, portalPagos: this.portalPagos })
    this.sedes = await this.portalPagos.getSedes(this.user.cuitCuil.numero)
    this.isLoading = false
  },

  computed: {
    ...mapState({
      estadosUso: state => state.app.estadosUso,
      estadosPago: state => state.app.estadosPago,
    }),
    comprobante() {
      return this.comprobanteTerceros?.comprobante
    },
    comprobanteUsado() {
      return this.comprobanteTerceros?.uso.estado_id == 'USADO'
    },
    comprobantePago() {
      return this.comprobanteTerceros?.pago.estado_id == 'PAGO'
    },
    sedesElegibles() {
      return this.sedes.filter(s => s.id.trim() !== this.comprobanteTerceros.sede.id.trim())
    },
  },

  methods: {
    ...mapActions({
      fetchEstadosUso: 'app/fetchEstadosUso',
      fetchEstadosPago: 'app/fetchEstadosPago',
    }),
    async buscarComprobante() {
      this.isLoading = true
      this.error = null
      this.nuevaSede = null
      try {
        this.comprobanteTerceros = await this.portalPagos.getComprobanteTerceros(
          this.user.cuitCuil.numero,
          this.numeroComprobante
        )
      } catch (error) {
        this.error = true
      } finally {
        this.isLoading = false
      }
    },
    textoEstadoUso(estadoId) {
      return this.estadosUso?.find(e => e.id.trim() == estadoId).descripcion
    },
    textoEstadoPago(estadoId) {
      return this.estadosPago?.find(e => e.id.trim() == estadoId).descripcion
    },
    isEstadoPago(estadoId) {
      return this.comprobanteTerceros.pago.estado_id == estadoId
    },
    tipoSede(sede_id) {
      switch (sede_id) {
        case '1':
          return 'Delegación'
        case '2':
          return 'Sede Central'
        case '3':
          return 'Oficina Delegada'
        case '4':
          return 'Oficina de venta de materiales'
        default:
          return 'Delegación'
      }
    },
    async guardarNuevaSede(done) {
      try {
        await this.portalPagos.patchComprobante(
          this.user.cuitCuil.numero,
          this.comprobante.numero_comprobante,
          {
            tipo: 'UPDT_SEDE',
            sede_id: this.nuevaSede.id,
          }
        )

        done()
        this.buscarComprobante()
        this.showConfirmModal = false

        this.$toast.open({
          message: 'El comprobante fue transferido a la nueva sede.',
          type: 'success',
          duration: 2500,
          dismissible: true,
        })
      } catch (e) {
        console.log(e)
        done('Ocurrió un error al procesar la información.')
      }
    },
  },
}
</script>
